import React, { useMemo, useState } from "react";
import ExcelJS from "exceljs";
import MySkeleton from "components/ui/feedback/Skeleton";
import { useStatsFetch } from "./useStatsApi";
import {
  StatCard,
  SectionCard,
  RefreshButton,
  ErrorMessage,
  Badge,
  formatNumber,
} from "./StatComponents";

const DOMAIN_META = [
  { key: "okul", label: "Okul servisi", color: "#1565c0", bg: "#e3f2fd" },
  { key: "personel", label: "Personel servisi", color: "#e65100", bg: "#fff3e0" },
];

const KIND_META = [
  { key: "vehicleRoute", label: "Öğrenci servisi", short: "Öğrenci", domain: "okul" },
  { key: "personelService", label: "Personel servisi", short: "Personel", domain: "personel" },
  { key: "bundledRoute", label: "Toplu öğrenci servisi", short: "Toplu", domain: "okul" },
];

const RECOMMENDATION_META = {
  silme_adayi: { label: "Silme adayı", bg: "#ffebee", color: "#c62828" },
  incele: { label: "İncele", bg: "#fff8e1", color: "#f9a825" },
  yeni: { label: "Yeni kayıt", bg: "#e3f2fd", color: "#1565c0" },
};

const DAY_OPTIONS = [7, 30, 90];

const formatDateTime = (iso) => {
  if (!iso) return "—";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString("tr-TR");
};

const formatDayStamp = () => {
  const now = new Date();
  const yyyy = now.getFullYear();
  const mm = String(now.getMonth() + 1).padStart(2, "0");
  const dd = String(now.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const slugCompany = (name) =>
  String(name || "sirket")
    .trim()
    .replace(/[<>:"/\\|?*]+/g, "")
    .replace(/\s+/g, "-")
    .slice(0, 50) || "sirket";

const selectStyle = {
  width: "100%",
  padding: "8px 10px",
  borderRadius: "4px",
  border: "1px solid #ccc",
  fontSize: "14px",
  background: "#fff",
};

const SortHeader = ({ label, sortKey, currentKey, dir, onSort, align = "left" }) => {
  const active = currentKey === sortKey;
  return (
    <th
      onClick={() => onSort(sortKey)}
      style={{
        padding: "10px",
        textAlign: align,
        borderBottom: "2px solid #ddd",
        whiteSpace: "nowrap",
        cursor: "pointer",
        userSelect: "none",
        color: active ? "#1976d2" : "#333",
      }}
    >
      {label}
      {active ? (dir === "desc" ? " ↓" : " ↑") : ""}
    </th>
  );
};

const tdStyle = {
  padding: "8px 10px",
  borderBottom: "1px solid #eee",
  verticalAlign: "middle",
};

const filterButtonStyle = (active) => ({
  padding: "6px 12px",
  borderRadius: "4px",
  border: active ? "1px solid #1976d2" : "1px solid #ccc",
  background: active ? "#1976d2" : "#fff",
  color: active ? "#fff" : "#333",
  cursor: "pointer",
  fontSize: "13px",
});

const checkboxLabelStyle = {
  display: "flex",
  alignItems: "center",
  gap: "6px",
  fontSize: "13px",
  color: "#444",
  whiteSpace: "nowrap",
};

const DomainBadge = ({ domain, label }) => {
  const meta = DOMAIN_META.find((item) => item.key === domain) || DOMAIN_META[0];
  return (
    <Badge color={meta.bg} textColor={meta.color}>
      {label || meta.label}
    </Badge>
  );
};

const RecommendationBadge = ({ value }) => {
  const meta = RECOMMENDATION_META[value] || RECOMMENDATION_META.incele;
  return (
    <Badge color={meta.bg} textColor={meta.color}>
      {meta.label}
    </Badge>
  );
};

const downloadWorkbook = async (workbook, fileName) => {
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = fileName;
  link.click();
  URL.revokeObjectURL(link.href);
};

const buildExcel = async ({ rows, companyName, days, from, to }) => {
  const workbook = new ExcelJS.Workbook();
  workbook.creator = "Routebot Admin";
  workbook.created = new Date();

  const summary = workbook.addWorksheet("Özet");
  summary.columns = [
    { header: "Alan", key: "field", width: 36 },
    { header: "Değer", key: "value", width: 50 },
  ];
  summary.addRows([
    { field: "Şirket", value: companyName || "" },
    { field: "Rapor tarihi", value: new Date().toLocaleString("tr-TR") },
    { field: "Pencere (gün)", value: days },
    { field: "Başlangıç", value: from ? formatDateTime(from) : "" },
    { field: "Bitiş", value: to ? formatDateTime(to) : "" },
    {
      field: "Tanım",
      value:
        "Aktif (isActive ≠ false) PersonelService / VehicleRoute / BundledRoute kayıtları. Kullanılmayan = son pencerede startedAt’li travel yok. Check isteği travel kaydetmez.",
    },
    { field: "Listedeki kayıt", value: rows.length },
    { field: "Okul servisi", value: rows.filter((row) => row.domain === "okul").length },
    { field: "Personel servisi", value: rows.filter((row) => row.domain === "personel").length },
    { field: "Silme adayı", value: rows.filter((row) => row.recommendation === "silme_adayi").length },
    { field: "Hiç çalışmamış", value: rows.filter((row) => row.neverRan).length },
  ]);
  summary.getRow(1).font = { bold: true };

  const sheet = workbook.addWorksheet("Kullanılmayan servisler");
  sheet.columns = [
    { header: "Şirket", key: "company", width: 28 },
    { header: "Demo", key: "demo", width: 10 },
    { header: "Şirket aktif", key: "companyActive", width: 14 },
    { header: "Servis alanı", key: "domainLabel", width: 18 },
    { header: "Kayıt türü", key: "kindLabel", width: 22 },
    { header: "Servis tipi", key: "serviceTypeLabel", width: 20 },
    { header: "Yön", key: "routeTypeLabel", width: 12 },
    { header: "Servis adı", key: "name", width: 32 },
    { header: "Okul / işyeri", key: "school", width: 28 },
    { header: "Kurum tipi", key: "schoolTypeLabel", width: 14 },
    { header: "Plaka", key: "plate", width: 14 },
    { header: "Sürücüler", key: "drivers", width: 28 },
    { header: "Yolcu sayısı", key: "passengerCount", width: 14 },
    { header: "Boş kadro", key: "emptyRoster", width: 12 },
    { header: "Vardiya / tek sefer", key: "oneOff", width: 18 },
    { header: "Tarih", key: "date", width: 14 },
    { header: "Tarihi geçmiş tek sefer", key: "oneOffPast", width: 22 },
    { header: "Yapıldığı günler", key: "serviceDaysLabel", width: 22 },
    { header: "Saat", key: "startTime", width: 10 },
    { header: "Varış", key: "arrivalTime", width: 10 },
    { header: "Oluşturma", key: "createdAt", width: 20 },
    { header: "Son güncelleme", key: "updatedAt", width: 20 },
    { header: "Son sefer", key: "lastStartedAt", width: 20 },
    { header: "Son sefer günü", key: "lastTravelDay", width: 16 },
    { header: "Toplam sefer (ömür)", key: "lifetimeTravelCount", width: 18 },
    { header: "Idle gün", key: "idleDays", width: 12 },
    { header: "Hiç çalışmamış", key: "neverRan", width: 16 },
    { header: "Yeni kayıt", key: "recentlyCreated", width: 12 },
    { header: "Öneri", key: "recommendation", width: 16 },
    { header: "Gerekçeler", key: "reasons", width: 50 },
    { header: "Servis ID", key: "id", width: 28 },
  ];

  rows.forEach((row) => {
    sheet.addRow({
      company: row.company,
      demo: row.isDemo ? "Evet" : "Hayır",
      companyActive: row.companyActive === false ? "Pasif" : "Aktif",
      domainLabel: row.domainLabel,
      kindLabel: row.kindLabel,
      serviceTypeLabel: row.serviceTypeLabel,
      routeTypeLabel: row.routeTypeLabel,
      name: row.name,
      school: row.school,
      schoolTypeLabel: row.schoolTypeLabel,
      plate: row.plate,
      drivers: (row.driverNames || []).join(", "),
      passengerCount: row.passengerCount,
      emptyRoster: row.emptyRoster ? "Evet" : "Hayır",
      oneOff: row.oneOff ? "Evet" : "Hayır",
      date: row.date,
      oneOffPast: row.oneOffPast ? "Evet" : "Hayır",
      serviceDaysLabel: row.serviceDaysLabel,
      startTime: row.startTime,
      arrivalTime: row.arrivalTime,
      createdAt: formatDateTime(row.createdAt),
      updatedAt: formatDateTime(row.updatedAt),
      lastStartedAt: formatDateTime(row.lastStartedAt),
      lastTravelDay: row.lastTravelDay || "—",
      lifetimeTravelCount: row.lifetimeTravelCount,
      idleDays: row.idleDays == null ? "" : row.idleDays,
      neverRan: row.neverRan ? "Evet" : "Hayır",
      recentlyCreated: row.recentlyCreated ? "Evet" : "Hayır",
      recommendation: RECOMMENDATION_META[row.recommendation]?.label || row.recommendation,
      reasons: (row.recommendationReasons || []).join(" | "),
      id: row.id,
    });
  });
  sheet.getRow(1).font = { bold: true };
  sheet.views = [{ state: "frozen", ySplit: 1 }];

  const fileName = `kullanilmayan-servisler-${slugCompany(companyName)}-${formatDayStamp()}.xlsx`;
  await downloadWorkbook(workbook, fileName);
};

const UnusedServicesSection = () => {
  const [days, setDays] = useState(30);
  const [companyId, setCompanyId] = useState("");
  const [companySearch, setCompanySearch] = useState("");
  const { data: listData, isLoading: isListLoading, error: listError } = useStatsFetch(
    "/admin/statistics/schools-companies",
  );
  const { data, isLoading, error, load } = useStatsFetch(
    `/admin/statistics/unused-services?days=${days}&companyId=${companyId}`,
    Boolean(companyId),
  );
  const [domainFilter, setDomainFilter] = useState("");
  const [kindFilter, setKindFilter] = useState("");
  const [search, setSearch] = useState("");
  const [hideRecentlyCreated, setHideRecentlyCreated] = useState(true);
  const [onlyDeleteCandidates, setOnlyDeleteCandidates] = useState(false);
  const [sortKey, setSortKey] = useState("name");
  const [sortDir, setSortDir] = useState("asc");
  const [expandedId, setExpandedId] = useState(null);
  const [excelBusy, setExcelBusy] = useState(false);

  const companies = useMemo(() => {
    const list = [...(listData?.companies || [])];
    list.sort((a, b) => String(a.name || "").localeCompare(String(b.name || ""), "tr"));
    const query = companySearch.trim().toLowerCase();
    if (!query) return list;
    return list.filter((company) => {
      if (company.id === companyId) return true;
      return String(company.name || "")
        .toLowerCase()
        .includes(query);
    });
  }, [listData, companySearch, companyId]);

  const selectedCompany = useMemo(
    () => (listData?.companies || []).find((company) => company.id === companyId) || data?.company || null,
    [listData, companyId, data],
  );

  const rows = useMemo(() => {
    const list = (data?.services || []).filter((row) => {
      if (data?.companyId && data.companyId !== companyId) return false;
      if (domainFilter && row.domain !== domainFilter) return false;
      if (kindFilter && row.kind !== kindFilter) return false;
      if (hideRecentlyCreated && row.recentlyCreated) return false;
      if (onlyDeleteCandidates && row.recommendation !== "silme_adayi") return false;
      if (search.trim()) {
        const q = search.trim().toLowerCase();
        const haystack = [row.name, row.school, row.plate, row.kindLabel, row.domainLabel]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      return true;
    });

    const factor = sortDir === "asc" ? 1 : -1;
    list.sort((a, b) => {
      if (sortKey === "company" || sortKey === "name" || sortKey === "school") {
        const cmp = String(a[sortKey] || "").localeCompare(String(b[sortKey] || ""), "tr");
        if (cmp !== 0) return factor * cmp;
        return String(a.name).localeCompare(String(b.name), "tr");
      }
      if (sortKey === "idleDays" || sortKey === "passengerCount" || sortKey === "lifetimeTravelCount") {
        const av = a[sortKey] == null ? -1 : a[sortKey];
        const bv = b[sortKey] == null ? -1 : b[sortKey];
        return factor * (av - bv);
      }
      return factor * String(a[sortKey] || "").localeCompare(String(b[sortKey] || ""), "tr");
    });
    return list;
  }, [data, companyId, domainFilter, kindFilter, hideRecentlyCreated, onlyDeleteCandidates, search, sortKey, sortDir]);

  const visibleStats = useMemo(
    () => ({
      unused: rows.length,
      okul: rows.filter((row) => row.domain === "okul").length,
      personel: rows.filter((row) => row.domain === "personel").length,
      neverRan: rows.filter((row) => row.neverRan).length,
      emptyRoster: rows.filter((row) => row.emptyRoster).length,
      deleteCandidates: rows.filter((row) => row.recommendation === "silme_adayi").length,
    }),
    [rows],
  );

  const onSort = (key) => {
    if (sortKey === key) {
      setSortDir((prev) => (prev === "desc" ? "asc" : "desc"));
      return;
    }
    setSortKey(key);
    setSortDir(key === "company" || key === "name" || key === "school" ? "asc" : "desc");
  };

  const exportExcel = async () => {
    if (!companyId || excelBusy || data?.companyId !== companyId) return;
    setExcelBusy(true);
    try {
      await buildExcel({
        rows,
        companyName: selectedCompany?.name || data?.company?.name || "",
        days: data?.days,
        from: data?.from,
        to: data?.to,
      });
    } finally {
      setExcelBusy(false);
    }
  };

  return (
    <div>
      <SectionCard title="Şirket">
        {isListLoading && !listData ? (
          <MySkeleton lineCount={2} />
        ) : (
          <div style={{ display: "flex", flexWrap: "wrap", gap: "12px", alignItems: "flex-end" }}>
            <div style={{ flex: "1 1 220px", minWidth: "180px" }}>
              <label style={{ fontSize: "13px", color: "#555" }}>Şirket ara</label>
              <input
                value={companySearch}
                onChange={(event) => setCompanySearch(event.target.value)}
                placeholder="Şirket adı..."
                style={{ ...selectStyle, boxSizing: "border-box" }}
              />
            </div>
            <div style={{ flex: "2 1 320px", minWidth: "240px" }}>
              <label style={{ fontSize: "13px", color: "#555" }}>Rapor şirketi</label>
              <select value={companyId} onChange={(event) => setCompanyId(event.target.value)} style={selectStyle}>
                <option value="">Şirket seçin...</option>
                {companies.map((company) => (
                  <option key={company.id} value={company.id}>
                    {company.name}
                    {company.isDemo ? " (Demo)" : ""}
                    {company.isActive === false ? " (Pasif)" : ""}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}
      </SectionCard>

      <ErrorMessage message={listError} />

      {!companyId ? (
        <p style={{ color: "#666", fontSize: "13px", marginTop: 0 }}>
          Raporu müşteriye şirket bazlı göndermek için önce şirketi seçin. Liste ve Excel yalnızca o şirketin aktif,
          son dönemde sefer başlatılmamış servislerini içerir.
        </p>
      ) : (
        <>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: "10px",
          marginBottom: "15px",
        }}
      >
        <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
          {DAY_OPTIONS.map((option) => (
            <button key={option} onClick={() => setDays(option)} style={filterButtonStyle(days === option)}>
              Son {option} gün
            </button>
          ))}
        </div>
        <div style={{ display: "flex", gap: "8px", alignItems: "center", flexWrap: "wrap" }}>
          <button
            onClick={exportExcel}
            disabled={!companyId || data?.companyId !== companyId || isLoading || excelBusy}
            style={{
              ...filterButtonStyle(false),
              border: "1px solid #2e7d32",
              color: "#2e7d32",
              opacity: !companyId || data?.companyId !== companyId || isLoading || excelBusy ? 0.5 : 1,
              cursor: !companyId || data?.companyId !== companyId || isLoading || excelBusy ? "not-allowed" : "pointer",
            }}
          >
            {excelBusy ? "Excel hazırlanıyor..." : "Excel'e aktar (bu şirket)"}
          </button>
          <RefreshButton onClick={() => load()} isLoading={isLoading} />
        </div>
      </div>

      <ErrorMessage message={error} />

      {isLoading && data?.companyId !== companyId ? (
        <MySkeleton lineCount={12} />
      ) : data?.companyId === companyId ? (
        <>
          <p style={{ color: "#666", fontSize: "13px", marginTop: 0, marginBottom: "12px" }}>
            <strong>{selectedCompany?.name || data?.company?.name || "Seçilen şirket"}</strong> için aktif öğrenci,
            personel ve toplu servisler taranır. <strong>Kullanılmayan</strong> = son {data.days} günde sefer
            başlatılmamış. Excel bu şirketin, aşağıdaki filtrelerin uygulandığı listesini indirir. Sayfa salt
            okumadır.
          </p>

          <div style={{ display: "flex", flexWrap: "wrap", gap: "15px", marginBottom: "16px" }}>
            <StatCard
              label="Kullanılmayan (filtreli)"
              value={visibleStats.unused}
              sub={`${formatNumber(data.totals?.unused)} ham / ${formatNumber(data.activeCounts?.total)} aktif`}
              color="#c62828"
            />
            <StatCard label="Okul servisi" value={visibleStats.okul} color="#1565c0" />
            <StatCard label="Personel servisi" value={visibleStats.personel} color="#e65100" />
            <StatCard label="Hiç çalışmamış" value={visibleStats.neverRan} color="#6a1b9a" />
            <StatCard label="Boş kadro" value={visibleStats.emptyRoster} color="#546e7a" />
            <StatCard label="Silme adayı" value={visibleStats.deleteCandidates} color="#ad1457" />
          </div>

          <div
            style={{
              display: "flex",
              gap: "8px",
              flexWrap: "wrap",
              alignItems: "center",
              marginBottom: "14px",
            }}
          >
            <button onClick={() => setDomainFilter("")} style={filterButtonStyle(!domainFilter)}>
              Tüm alanlar
            </button>
            {DOMAIN_META.map((item) => (
              <button
                key={item.key}
                onClick={() => {
                  setDomainFilter(item.key);
                  const kindMeta = KIND_META.find((kind) => kind.key === kindFilter);
                  if (kindMeta && kindMeta.domain !== item.key) setKindFilter("");
                }}
                style={filterButtonStyle(domainFilter === item.key)}
              >
                {item.label}
              </button>
            ))}
            <span style={{ color: "#bbb" }}>|</span>
            <button onClick={() => setKindFilter("")} style={filterButtonStyle(!kindFilter)}>
              Tüm türler
            </button>
            {KIND_META.map((item) => (
              <button
                key={item.key}
                title={item.label}
                onClick={() => {
                  setKindFilter(item.key);
                  setDomainFilter(item.domain);
                }}
                style={filterButtonStyle(kindFilter === item.key)}
              >
                {item.short}
              </button>
            ))}
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Servis, okul, plaka..."
              style={{
                padding: "6px 10px",
                border: "1px solid #ccc",
                borderRadius: "4px",
                fontSize: "13px",
                minWidth: "220px",
                marginLeft: "4px",
              }}
            />
          </div>

          <div style={{ display: "flex", gap: "16px", flexWrap: "wrap", marginBottom: "16px" }}>
            <label style={checkboxLabelStyle}>
              <input
                type="checkbox"
                checked={hideRecentlyCreated}
                onChange={(event) => setHideRecentlyCreated(event.target.checked)}
              />
              Yeni kayıtları gizle
            </label>
            <label style={checkboxLabelStyle}>
              <input
                type="checkbox"
                checked={onlyDeleteCandidates}
                onChange={(event) => setOnlyDeleteCandidates(event.target.checked)}
              />
              Yalnız silme adayları
            </label>
          </div>

          <SectionCard title={`Kullanılmayan servisler — ${selectedCompany?.name || data?.company?.name || ""} (${formatNumber(rows.length)})`}>
            {rows.length === 0 ? (
              <p style={{ color: "#999", margin: 0 }}>Bu filtrede kullanılmayan servis yok.</p>
            ) : (
              <div style={{ overflowX: "auto", maxHeight: "640px" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
                  <thead>
                    <tr style={{ background: "#f5f5f5" }}>
                      <SortHeader
                        label="Şirket"
                        sortKey="company"
                        currentKey={sortKey}
                        dir={sortDir}
                        onSort={onSort}
                      />
                      <th style={{ padding: "10px", textAlign: "left", borderBottom: "2px solid #ddd" }}>Alan</th>
                      <th style={{ padding: "10px", textAlign: "left", borderBottom: "2px solid #ddd" }}>Tür</th>
                      <SortHeader label="Servis" sortKey="name" currentKey={sortKey} dir={sortDir} onSort={onSort} />
                      <SortHeader label="Okul / işyeri" sortKey="school" currentKey={sortKey} dir={sortDir} onSort={onSort} />
                      <SortHeader
                        label="Yolcu"
                        sortKey="passengerCount"
                        currentKey={sortKey}
                        dir={sortDir}
                        onSort={onSort}
                        align="right"
                      />
                      <SortHeader
                        label="Idle gün"
                        sortKey="idleDays"
                        currentKey={sortKey}
                        dir={sortDir}
                        onSort={onSort}
                        align="right"
                      />
                      <th style={{ padding: "10px", textAlign: "left", borderBottom: "2px solid #ddd" }}>Öneri</th>
                      <th style={{ padding: "10px", textAlign: "center", borderBottom: "2px solid #ddd" }}>Detay</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => {
                      const isExpanded = expandedId === `${row.kind}:${row.id}`;
                      return (
                        <React.Fragment key={`${row.kind}:${row.id}`}>
                          <tr style={{ background: isExpanded ? "#e3f2fd" : "transparent" }}>
                            <td style={tdStyle}>
                              <div style={{ display: "flex", gap: "6px", alignItems: "center", flexWrap: "wrap" }}>
                                <span style={{ fontWeight: 500 }}>{row.company}</span>
                                {row.isDemo && (
                                  <Badge color="#fff3e0" textColor="#e65100">
                                    Demo
                                  </Badge>
                                )}
                              </div>
                            </td>
                            <td style={tdStyle}>
                              <DomainBadge domain={row.domain} label={row.domainLabel} />
                            </td>
                            <td style={tdStyle}>
                              <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                                <span>{row.kindLabel}</span>
                                <span style={{ color: "#888", fontSize: "12px" }}>{row.serviceTypeLabel}</span>
                              </div>
                            </td>
                            <td style={tdStyle}>
                              <div style={{ fontWeight: 500 }}>{row.name}</div>
                              <div style={{ color: "#888", fontSize: "12px" }}>
                                {[row.routeTypeLabel, row.plate].filter(Boolean).join(" · ") || "—"}
                              </div>
                            </td>
                            <td style={tdStyle}>
                              <div>{row.school || "—"}</div>
                              {row.schoolTypeLabel ? (
                                <div style={{ color: "#888", fontSize: "12px" }}>{row.schoolTypeLabel}</div>
                              ) : null}
                            </td>
                            <td style={{ ...tdStyle, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                              {formatNumber(row.passengerCount)}
                              {row.emptyRoster ? (
                                <div style={{ color: "#c62828", fontSize: "11px" }}>boş</div>
                              ) : null}
                            </td>
                            <td style={{ ...tdStyle, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                              {row.neverRan ? "hiç yok" : formatNumber(row.idleDays)}
                            </td>
                            <td style={tdStyle}>
                              <RecommendationBadge value={row.recommendation} />
                            </td>
                            <td style={{ ...tdStyle, textAlign: "center" }}>
                              <button
                                onClick={() => setExpandedId(isExpanded ? null : `${row.kind}:${row.id}`)}
                                style={{
                                  background: isExpanded ? "#1976d2" : "none",
                                  color: isExpanded ? "#fff" : "#333",
                                  border: "1px solid #ccc",
                                  padding: "4px 8px",
                                  borderRadius: "4px",
                                  cursor: "pointer",
                                  fontSize: "12px",
                                }}
                              >
                                {isExpanded ? "Kapat" : "Detay"}
                              </button>
                            </td>
                          </tr>
                          {isExpanded && (
                            <tr style={{ background: "#e3f2fd" }}>
                              <td colSpan={9} style={{ padding: "12px 15px", borderBottom: "1px solid #eee" }}>
                                <div style={{ background: "#fff", padding: "12px", borderRadius: "6px" }}>
                                  <h4 style={{ margin: "0 0 8px 0", fontSize: "14px" }}>
                                    {row.domainLabel} — {row.name}
                                  </h4>
                                  <ul
                                    style={{
                                      margin: "0 0 10px 0",
                                      paddingLeft: "18px",
                                      color: "#555",
                                      fontSize: "13px",
                                    }}
                                  >
                                    {(row.recommendationReasons || []).map((reason) => (
                                      <li key={reason}>{reason}</li>
                                    ))}
                                  </ul>
                                  <div
                                    style={{
                                      display: "grid",
                                      gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
                                      gap: "8px 16px",
                                      fontSize: "13px",
                                    }}
                                  >
                                    <div>
                                      <strong>Sürücü:</strong> {(row.driverNames || []).join(", ") || "—"}
                                    </div>
                                    <div>
                                      <strong>Son sefer:</strong> {formatDateTime(row.lastStartedAt)}
                                    </div>
                                    <div>
                                      <strong>Son sefer günü:</strong> {row.lastTravelDay || "—"}
                                    </div>
                                    <div>
                                      <strong>Ömür boyu sefer:</strong> {formatNumber(row.lifetimeTravelCount)}
                                    </div>
                                    <div>
                                      <strong>Oluşturma:</strong> {formatDateTime(row.createdAt)}
                                    </div>
                                    <div>
                                      <strong>Güncelleme:</strong> {formatDateTime(row.updatedAt)}
                                    </div>
                                    <div>
                                      <strong>Günler:</strong> {row.serviceDaysLabel || "—"}
                                    </div>
                                    <div>
                                      <strong>Saat:</strong> {row.startTime || "—"}
                                      {row.arrivalTime ? ` / varış ${row.arrivalTime}` : ""}
                                    </div>
                                    <div>
                                      <strong>Tek sefer tarihi:</strong> {row.date || "—"}
                                    </div>
                                    <div>
                                      <strong>Servis ID:</strong>{" "}
                                      <span style={{ color: "#888", fontSize: "12px" }}>{row.id}</span>
                                    </div>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </SectionCard>
        </>
      ) : null}
        </>
      )}
    </div>
  );
};

export default UnusedServicesSection;
